import React from "react";
import { TypeOf } from 'zod';
import {Save} from "@mui/icons-material";
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {Button, Stack, Box, Badge,} from "@mui/material";
import useEntity from "../../../hooks/useShipment";
import {PrimaryShipment} from "../../../types/models/Shipment";
import {useGetShipmentQuery} from "../../../apis/Shipment.api";
import ShipmentUpdateForm, {formSchema, closedFormSchema} from "../../../components/shipment/ShipmentForm/ShipmentUpdateForm";
import { Trans } from "@lingui/react/macro";
import {ShipmentState} from "../../../types/models/ShipmentState.d";

export type FormInput = TypeOf<typeof formSchema>;

const ShipmentUpdateFormProvider = ({ id, initialFormState, cancelButton = undefined, redirectPath = undefined, successAction = undefined }) => {

    const primary = PrimaryShipment.fromPath(id);
    const { data, isLoading } = useGetShipmentQuery(primary)

    const methods = useForm<FormInput>({
        resolver: (values, context, options) => {
            const schema = values.state === ShipmentState.Closed || values.state === ShipmentState.Finished
                ? closedFormSchema : formSchema;
            return zodResolver(schema)(values, context, options);
        },
        defaultValues: initialFormState(data)
    });
    const {  updateShipment } = useEntity();

    const {
        watch,
        trigger,
        reset,
        handleSubmit,
        formState: { isSubmitSuccessful, errors, },
    } = methods;

    React.useEffect(() => {
        if (data) {
            reset(data);
        }
    }, [data, reset]);

    const watchState = watch("state")

    React.useEffect(() => {
        trigger()
    }, [watchState, trigger])

    const onSubmitHandler: SubmitHandler<FormInput> = async (values) => {
        const data = await updateShipment({
            id: primary,
            data: values,
        }, redirectPath)
        if (data && successAction) {
            successAction(data)
        }
    };

    return (
        <>
            <FormProvider {...methods}>
                <Box
                    component='form'
                    onSubmit={
                        (e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleSubmit(onSubmitHandler)(e)
                        }
                    }
                    noValidate
                >

                    <ShipmentUpdateForm watch={watch}/>

                    <Stack direction="row" justifyContent="end" spacing={1} padding={5}>

                        { cancelButton }

                        
                        <Badge badgeContent={Object.values(errors).length} color="error" anchorOrigin={ {vertical: 'bottom'} }>
                            <Button size="medium" variant="contained" color="primary" type='submit' startIcon={<Save />}>
                                <Trans>Update</Trans>
                            </Button>
                        </Badge>
                    </Stack>
                </Box>
            </FormProvider>
        </>
    )
}

export default ShipmentUpdateFormProvider
